<template>
  <div class="updates-page">

    <div class="updates-hero">
      <div class="updates-container">
        <div class="updates-hero-inner">
          <div>
            <p class="updates-kicker">Workspace activity</p>
            <h1 data-testid="updates-page-title" class="updates-title">Activity History</h1>
            <p data-testid="updates-page-subtitle" class="updates-subtitle">
              Ask and Change requests across your sites
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="updates-container updates-content">
      <div v-if="loading" class="updates-empty">
        <div class="updates-spinner"></div>
        <p>Loading activity...</p>
      </div>

      <div v-else-if="updates.length > 0" class="updates-list">
        <div
          v-for="update in updates"
          :key="update.id"
          :data-testid="isAsk(update) ? 'ask-history-row' : 'change-history-row'"
          :data-request-type="update.request_type || ''"
          class="updates-card"
        >
          <div class="updates-row">
            <div class="updates-main">
              <div class="updates-meta">
                <span
                  data-testid="history-kind-badge"
                  :class="kindBadgeClass(update)"
                >
                  {{ kindLabel(update) }}
                </span>
                <span
                  data-testid="history-status-badge"
                  :class="statusClass(update)"
                >
                  {{ statusLabel(update) }}
                </span>
                <span class="updates-date">
                  {{ formatDate(update.created_at) }}
                </span>
              </div>
              <p class="updates-site">
                {{ update.site_name || 'Site' }}
              </p>
              <p class="updates-domain">
                {{ update.site_subdomain ? `${update.site_subdomain}.beyondwebsite.ai` : '—' }}
              </p>

              <!-- Ask rows render Q + A so the row is clearly question-answer
                   shape, not a change record. -->
              <template v-if="isAsk(update)">
                <div data-testid="ask-row-body" class="space-y-2">
                  <div>
                    <span class="updates-label">Question</span>
                    <p data-testid="ask-row-question" class="updates-copy updates-copy-strong">
                      {{ askQuestion(update) }}
                    </p>
                  </div>
                  <div v-if="askAnswer(update)">
                    <span class="updates-label">Answer</span>
                    <p data-testid="ask-row-answer" class="updates-copy updates-copy-muted whitespace-pre-line">
                      {{ askAnswer(update) }}
                    </p>
                  </div>
                </div>
              </template>
              <template v-else>
                <p data-testid="change-row-prompt" class="updates-copy updates-copy-muted">
                  {{ update.prompt }}
                </p>
              </template>
            </div>
            <div class="updates-actions">
              <router-link
                :to="`/sites/${update.site_id}/edit`"
                class="updates-link"
              >
                Open Editor →
              </router-link>
              <!-- Preview link is meaningful only for Change rows — Ask rows
                   never produce a preview build. -->
              <a
                v-if="!isAsk(update) && update.preview_url"
                :href="update.preview_url"
                target="_blank"
                class="updates-link"
              >
                View Preview →
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="updates-empty">
        No activity yet.
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { format } from 'date-fns'
import { useSitesStore } from '../stores/sites'

export default {
  name: 'Updates',
  components: {},
  setup() {
    const siteStore = useSitesStore()
    const updates = ref([])
    const loading = ref(false)

    // Type discriminator for the mixed history list. Ask rows are identified
    // by request_type='question' which is set by BWAIService.askQuestion
    // (Ask-mode AC005). Every other shape is treated as a change record.
    const isAsk = (update) => update?.request_type === 'question'

    const kindLabel = (update) => (isAsk(update) ? 'Ask' : 'Change')

    const kindBadgeClass = (update) => {
      if (isAsk(update)) {
        // Distinct from the Change palette so the row is visually unmistakable.
        return 'updates-badge updates-badge-ask'
      }
      return 'updates-badge updates-badge-change'
    }

    const statusLabel = (update) => {
      if (isAsk(update)) return 'Answered'
      return update?.status || '—'
    }

    const statusClass = (update) => {
      if (isAsk(update)) {
        return 'updates-badge updates-badge-answered'
      }
      const status = update?.status
      return {
        'updates-badge updates-badge-progress': status === 'pending' || status === 'implementing',
        'updates-badge updates-badge-preview': status === 'preview_ready',
        'updates-badge updates-badge-published': status === 'published',
        'updates-badge updates-badge-failed': status === 'failed' || status === 'reverted',
        'updates-badge updates-badge-upsell': status === 'requires_upsell'
      }
    }

    // Postgres jsonb columns surface as parsed objects via pg, but some code
    // paths (raw JSON column reads, hydrated cache) hand back strings. Cover
    // both shapes defensively.
    const parsePayload = (update) => {
      const raw = update?.structured_payload
      if (!raw) return null
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch (_) { return null }
      }
      if (typeof raw === 'object') return raw
      return null
    }

    const askQuestion = (update) => {
      const env = parsePayload(update)
      return (env && typeof env.question === 'string' && env.question) || update?.prompt || ''
    }

    const askAnswer = (update) => {
      const env = parsePayload(update)
      return (env && typeof env.answer === 'string') ? env.answer : ''
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) {
        return '—'
      }
      return format(date, 'MMM d, yyyy h:mm a')
    }

    const loadUpdates = async () => {
      loading.value = true
      try {
        const response = await siteStore.fetchAllUpdateRequests({ limit: 50 })
        updates.value = response.updates || []
      } catch (error) {
        console.error('Failed to load activity:', error)
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadUpdates()
    })

    return {
      updates,
      loading,
      isAsk,
      kindLabel,
      kindBadgeClass,
      statusLabel,
      statusClass,
      askQuestion,
      askAnswer,
      formatDate
    }
  }
}
</script>

<style scoped>
.updates-page {
  min-height: 100vh;
  color: var(--text-primary);
  background:
    radial-gradient(circle at 12% 0%, rgba(124,109,250,0.16), transparent 32rem),
    radial-gradient(circle at 86% 10%, rgba(22,163,74,0.09), transparent 28rem),
    var(--surface-base);
}

.updates-container {
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
}

.updates-hero {
  border-bottom: var(--glass-border);
  background: rgba(15,15,24,0.72);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

.updates-hero-inner {
  padding: 32px 0 28px;
}

.updates-kicker {
  margin: 0 0 8px;
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.updates-title {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  letter-spacing: -0.04em;
}

.updates-subtitle {
  margin: 8px 0 0;
  color: var(--text-secondary);
  font-size: 15px;
}

.updates-content {
  padding: 28px 0 48px;
}

.updates-list {
  display: grid;
  gap: 14px;
}

.updates-card {
  padding: 22px;
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  background: var(--glass-bg);
  box-shadow: var(--elev-sm);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

.updates-card:hover {
  border: var(--glass-border-hi);
  background: var(--glass-bg-hover);
}

.updates-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.updates-main {
  min-width: 0;
  flex: 1;
}

.updates-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.updates-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  border: var(--glass-border);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.updates-badge-ask,
.updates-badge-answered {
  color: #93c5fd;
  background: rgba(59,130,246,0.14);
}

.updates-badge-change,
.updates-badge-progress,
.updates-badge-upsell {
  color: #c4b5fd;
  background: rgba(124,109,250,0.18);
}

.updates-badge-preview {
  color: #fde68a;
  background: rgba(245,158,11,0.14);
}

.updates-badge-published {
  color: #86efac;
  background: rgba(34,197,94,0.14);
}

.updates-badge-failed {
  color: #fca5a5;
  background: rgba(239,68,68,0.14);
}

.updates-date,
.updates-domain,
.updates-copy-muted {
  color: var(--text-muted);
}

.updates-site {
  margin: 0 0 4px;
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 700;
}

.updates-domain {
  margin: 0 0 14px;
  font-size: 12px;
}

.updates-label {
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.updates-copy {
  margin: 4px 0 0;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.65;
}

.updates-copy-strong {
  color: var(--text-primary);
}

.updates-actions {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: max-content;
}

.updates-link {
  color: var(--accent);
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.updates-link:hover {
  color: var(--text-primary);
}

.updates-empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: var(--text-secondary);
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
}

.updates-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid rgba(255,255,255,0.16);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 720px) {
  .updates-row {
    flex-direction: column;
  }

  .updates-actions {
    align-items: flex-start;
  }
}
</style>
